import React from "react"
import utilStyles from "../../asset/styles/util/util.module.scss"
import Grid from "@mui/material/Grid"
import Table, { TableColumn } from "component/table/table"
import InputText from "component/inputText/inputText"
import Toggle from "component/toggle/Toggle"
import styles from "../../asset/styles/dayTripDetail/dayTripDetail.module.scss"
import { DayTripDetailModel } from "./dayTripDetailTypes"

/** Row shape for seating table: one row with keys "4"|"7"|... and price values */
type SeatingPricingRow = Record<string, number>

interface DayTripSeatingPricingProps {
    data: DayTripDetailModel
    onChange: (updates: Partial<DayTripDetailModel>) => void
    error?: string
    sicPriceError?: string
}

const DayTripSeatingPricing: React.FC<DayTripSeatingPricingProps> = ({
    data,
    onChange,
    error,
    sicPriceError,
}) => {
    const seatingPrices = data.seating_prices ?? []

    const updateSeatingPrice = (capacity: number, value: number) => {
        const updated = seatingPrices.map((p) =>
            p.capacity === capacity ? { ...p, price_per_vehicle: value } : p
        )
        onChange({ seating_prices: updated })
    }

    const getPriceForCapacity = (capacity: number) => {
        const item = seatingPrices.find((p) => p.capacity === capacity)
        return item?.price_per_vehicle ?? 0
    }

    const capacities =
        seatingPrices.length > 0
            ? seatingPrices.map((p) => p.capacity)
            : [4, 7, 16, 29, 35, 45]
    const colWidth = capacities.length > 0 ? `${100 / capacities.length}%` : "16.66%"

    const pricingRow: SeatingPricingRow = capacities.reduce(
        (acc, cap) => {
            acc[String(cap)] = getPriceForCapacity(cap)
            return acc
        },
        {} as SeatingPricingRow
    )

    const columns: TableColumn<SeatingPricingRow>[] = capacities.map(
        (capacity) => {
            const price = getPriceForCapacity(capacity)
            const hasError = price == null || !Number.isFinite(price) || price <= 0
            return {
                key: String(capacity),
                label: (
                    <>
                        {capacity} Seater{" "}
                        <span style={{ color: "#dc3545" }}>*</span>
                    </>
                ),
                align: "left" as const,
                width: colWidth,
                render: (value: number) => {
                    const displayValue =
                        value == null || !Number.isFinite(value) || value < 0
                            ? ""
                            : String(value)
                    return (
                        <InputText
                            label=""
                            value={displayValue}
                            onChange={(e) =>
                                updateSeatingPrice(
                                    capacity,
                                    Number(e.target.value) || 0
                                )
                            }
                            placeholder="Price per vehicle"
                            type="number"
                            // required
                            error={error && hasError ? "Required" : undefined}
                        />
                    )
                },
            }
        }
    )

    // ---- Dinner Transfer pricing (UI-only for now; mirrors the main per-vehicle table) ----
    const DEFAULT_CAPS = [4, 7, 16, 29, 35, 45]
    const dinnerPrices = data.dinner_transfer_prices ?? []
    const updateDinnerPrice = (capacity: number, value: number) => {
        // Seed all capacity rows on first edit so values aren't dropped when the array is empty.
        const base =
            dinnerPrices.length > 0
                ? dinnerPrices
                : DEFAULT_CAPS.map((c) => ({ capacity: c, price_per_vehicle: 0 }))
        const updated = base.some((p) => p.capacity === capacity)
            ? base.map((p) => (p.capacity === capacity ? { ...p, price_per_vehicle: value } : p))
            : [...base, { capacity, price_per_vehicle: value }]
        onChange({ dinner_transfer_prices: updated })
    }
    const getDinnerPriceForCapacity = (capacity: number) => {
        const item = dinnerPrices.find((p) => p.capacity === capacity)
        return item?.price_per_vehicle ?? 0
    }
    const dinnerCapacities = dinnerPrices.length > 0 ? dinnerPrices.map((p) => p.capacity) : DEFAULT_CAPS
    // The SIC (per-person) column is only shown when BOTH toggles are on; it's always last.
    const showDinnerSicColumn = Boolean(data.flag_enable_sic && data.flag_enable_dinner)
    const dinnerColCount = dinnerCapacities.length + (showDinnerSicColumn ? 1 : 0)
    const dinnerColWidth = dinnerColCount > 0 ? `${100 / dinnerColCount}%` : "16.66%"

    const dinnerPricingRow: SeatingPricingRow = dinnerCapacities.reduce(
        (acc, cap) => {
            acc[String(cap)] = getDinnerPriceForCapacity(cap)
            return acc
        },
        {} as SeatingPricingRow
    )
    if (showDinnerSicColumn) {
        dinnerPricingRow["sic"] = data.dinner_transfer_sic_price_per_person ?? 0
    }

    const toDisplay = (value: number) =>
        value == null || !Number.isFinite(value) || value < 0 ? "" : String(value)

    const dinnerColumns: TableColumn<SeatingPricingRow>[] = [
        ...dinnerCapacities.map((capacity) => ({
            key: String(capacity),
            label: <>{capacity} Seater</>,
            align: "left" as const,
            width: dinnerColWidth,
            render: (value: number) => (
                <InputText
                    label=""
                    value={toDisplay(value)}
                    onChange={(e) => updateDinnerPrice(capacity, Number(e.target.value) || 0)}
                    placeholder="Price per vehicle"
                    type="number"
                />
            ),
        })),
        ...(showDinnerSicColumn
            ? [
                  {
                      key: "sic",
                      label: <>SIC</>,
                      align: "left" as const,
                      width: dinnerColWidth,
                      render: (value: number) => (
                          <InputText
                              label=""
                              value={toDisplay(value)}
                              onChange={(e) =>
                                  onChange({
                                      dinner_transfer_sic_price_per_person: Number(e.target.value) || 0,
                                  })
                              }
                              placeholder="Price per person"
                              type="number"
                          />
                      ),
                  } as TableColumn<SeatingPricingRow>,
              ]
            : []),
    ]

    return (
        <div className={utilStyles.pageContainerContent}>
            <div className={styles.sectionHeader}>
                <h2 className={styles.sectionTitle}>Seating & Pricing</h2>
            </div>
            {error && (
                <div style={{ color: "#dc3545", fontSize: 13, marginBottom: 8 }}>
                    {error}
                </div>
            )}
            <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 12 }}>
                    <Table<SeatingPricingRow>
                        minWidth={100}
                        columns={columns}
                        data={[pricingRow]}
                        keyExtractor={() => "seating-pricing"}
                        className={styles.seatingTable}
                    />
                </Grid>
                <Grid size={{ xs: 12, md: 12 }}>
                    <div className={styles.sicSection}>
                        <div className={styles.sicToggleRow}>
                            <Toggle
                                rightLabel="Enable SIC"
                                checked={data.flag_enable_sic}
                                onChange={(checked) =>
                                    onChange({ flag_enable_sic: checked })
                                }
                            />
                        </div>
                        {data.flag_enable_sic && (
                            <div style={{ maxWidth: 320 }}>
                                <InputText
                                    label="SIC Price per Person"
                                    value={
                                        data.sic_price_per_person < 0 ||
                                        data.sic_price_per_person == null
                                            ? ""
                                            : String(
                                                  data.sic_price_per_person
                                              )
                                    }
                                    onChange={(e) => {
                                        const raw = e.target.value
                                        if (raw === "" || raw === null || raw === undefined) {
                                            onChange({ sic_price_per_person: 0 })
                                            return
                                        }
                                        const num = Number(raw)
                                        if (!Number.isFinite(num) || num < 0) return
                                        onChange({ sic_price_per_person: num })
                                    }}
                                    placeholder="Enter Price per person"
                                    type="number"
                                    required
                                    error={sicPriceError}
                                />
                            </div>
                        )}
                    </div>
                </Grid>
                <Grid size={{ xs: 12, md: 12 }}>
                    <div className={styles.sicSection}>
                        <div className={styles.sicToggleRow}>
                            <Toggle
                                rightLabel="Enable Dinner Transfer"
                                checked={data.flag_enable_dinner ?? false}
                                onChange={(checked) =>
                                    onChange({ flag_enable_dinner: checked })
                                }
                            />
                        </div>
                    </div>
                </Grid>
                {data.flag_enable_dinner && (
                    <Grid size={{ xs: 12, md: 12 }}>
                        <Table<SeatingPricingRow>
                            minWidth={100}
                            columns={dinnerColumns}
                            data={[dinnerPricingRow]}
                            keyExtractor={() => "dinner-transfer-pricing"}
                            className={styles.seatingTable}
                        />
                    </Grid>
                )}
            </Grid>
        </div>
    )
}

export default DayTripSeatingPricing
